/**
 * Lessons: the game teaching itself, one modal at a time, at the moment each thing happens. The
 * same lessons serve Learn to Play (the scripted tutorial round, `view.learn`) and Beginner Mode in
 * any real game, where each shows once per game (the ✕ skips the rest of the game's).
 *
 * When each shows (`wanted`), in this order:
 * - A reveal (play paused for you, `waitingFor`): Your Deck and The Flip (first reveal only), then
 *   War! at a war; at play starting, Vulnerabilities, Powers (in a real game again whenever your
 *   card is new), Turn Order and Rounds.
 * - Your war roll: Roll for War (the first), A Tie! (a war that went past one die).
 * - Your turn before the roll: Your Turn (Learn to Play's second turn: Your Next Turn).
 * - After your roll (once the dice land): Make a Kill when your dice could destroy a card (then
 *   Kill Now or Wait?), else Save Dice. Learn to Play follows its script instead (`coach.ts`):
 *   Save Dice on turn 1; on turn 2 Make a Kill, Use Your Power once the natural kill is held, and
 *   Kill Now or Wait? once the power's die is held.
 * - Round Over: after its banner (`roundsOver`). In Learn to Play this ends the tutorial.
 *
 * Nothing shows while something is playing out or a full-screen screen is up (`blocked`); a lesson
 * already up stays. `pending`: a lesson is due (up or waiting its turn), so the table holds play
 * for it (the reveal's `introDone` waits, the turn buttons stay hidden).
 */
import { useState, type ReactNode } from 'react';
import { MAX_SEQUENCES, t, type CardDef, type GameView } from '@kadice/shared';
import { PowerLine } from '../beginner';
import type { CoachStage } from './coach';

export type LessonId =
  | 'deck'
  | 'flip'
  | 'war'
  | 'warRoll'
  | 'warTie'
  | 'vuln'
  | 'power'
  | 'order'
  | 'round'
  | 'first'
  | 'rollRest'
  | 'save'
  | 'kill'
  | 'powerUse'
  | 'greed'
  | 'win';

interface Wanted {
  id: LessonId;
  /** Shown once per key (Powers in a real game: once per card). */
  key: string;
}

export interface LessonInput {
  view: GameView;
  /** Lessons on: Beginner Mode (not skipped this game), or Learn to Play. */
  enabled: boolean;
  /** Your turn, and the roll has landed (or not been made). */
  myTurn: boolean;
  /** The dice on the table could destroy a card. */
  couldKill: boolean;
  /** Learn to Play: where your scripted turn is. */
  coach: CoachStage | null;
  /** Round Over banners closed so far this game. */
  roundsOver: number;
}

const POWER_STAGES: readonly CoachStage[] = ['power', 'holdPower', 'targets', 'attack'];
const TARGET_STAGES: readonly CoachStage[] = ['targets', 'attack'];

function wanted({ view: v, myTurn, couldKill, coach, roundsOver }: LessonInput): Wanted[] {
  const me = v.you.id;
  const self = v.players.find((p) => p.id === me);
  if (v.you.spectating || !self) return [];
  const learn = !!v.learn;
  const out: Wanted[] = [];
  const add = (id: LessonId, key: string = id) => out.push({ id, key });
  const reveal = v.waitingFor.includes(me);
  const free = v.waitingFor.length === 0;
  if (reveal && (v.phase === 'war' || v.phase === 'playing')) {
    add('deck');
    add('flip');
  }
  if (v.phase === 'war' && v.war) {
    if (reveal) add('war');
    if (free && v.war.participants.includes(me) && !v.war.rolls[me]) add(v.war.round > 1 ? 'warTie' : 'warRoll');
  }
  if (v.phase === 'playing' && reveal && self.card !== null) {
    add('vuln');
    add('power', learn ? 'power' : `power:${self.card}`);
    add('order');
    add('round');
  }
  const turn = v.turn;
  if (myTurn && free && turn && !turn.ghost) {
    if (!turn.hasRolled) add(learn && v.sequence > 1 ? 'rollRest' : 'first');
    else if (learn) {
      if (v.sequence === 1) add('save');
      else {
        add('kill');
        if (coach && POWER_STAGES.includes(coach)) add('powerUse');
        if (coach && TARGET_STAGES.includes(coach)) add('greed');
      }
    } else if (couldKill) {
      add('kill');
      add('greed');
    } else add('save');
  }
  if (roundsOver > 0) add('win');
  return out;
}

const SEEN_KEY = 'kadice.lessons';

/** Lessons seen in one game, kept across reloads (the last game only). */
function loadSeen(gameId: number): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(SEEN_KEY) ?? 'null') as { gameId: number; seen: string[] } | null;
    return saved?.gameId === gameId && Array.isArray(saved.seen) ? saved.seen : [];
  } catch {
    return [];
  }
}
function saveSeen(gameId: number, seen: string[]) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify({ gameId, seen }));
  } catch {
    // Storage blocked: lessons still show once per visit.
  }
}

export function useLessons(
  input: LessonInput,
  blocked: boolean,
): { lesson: LessonId | null; pending: boolean; done: () => void } {
  const gameId = input.view.gameId;
  const [state, setState] = useState(() => ({ gameId, seen: loadSeen(gameId), up: null as string | null }));
  const current = state.gameId === gameId ? state : { gameId, seen: loadSeen(gameId), up: null };
  if (current !== state) setState(current);
  const next = input.enabled ? wanted(input).find((w) => !current.seen.includes(w.key)) : undefined;
  // Once up, a lesson stays until Next; a new one waits until nothing is playing.
  const show = !!next && (current.up === next.key || !blocked);
  const up = show ? next.key : null;
  if (up !== current.up) setState({ ...current, up });
  const done = () => {
    if (!next) return;
    const seen = [...current.seen, next.key];
    saveSeen(gameId, seen);
    setState({ gameId, seen, up: null });
  };
  return { lesson: show ? next.id : null, pending: !!next, done };
}

/** The tutorial's own line under a lesson (what to do now), by lesson. */
const HOW_LEARN: Partial<Record<LessonId, string>> = {
  deck: 'learn.deckHow',
  flip: 'learn.flipHow',
  warRoll: 'learn.warRollHow',
  warTie: 'learn.warTieHow',
  vuln: 'learn.vulnHow',
  power: 'learn.powerHow',
  order: 'learn.orderHow',
  first: 'learn.firstHow',
  save: 'learn.saveHow',
  kill: 'learn.killHow',
  greed: 'learn.greedHow',
  win: 'learn.winHow',
};
/** A real game's line under a lesson. */
const HOW_GAME: Partial<Record<LessonId, string>> = {
  first: 'learn.firstGeneric',
  save: 'learn.saveGeneric',
  kill: 'learn.killGeneric',
};
/** Lessons whose body is the instruction itself. */
const BODY: Partial<Record<LessonId, string>> = {
  rollRest: 'learn.rollRest',
  powerUse: 'learn.powerUse',
};
/** Turn lessons sit at the top (your dice and buttons are at the bottom); the rest at the bottom. */
const TOP: readonly LessonId[] = ['first', 'rollRest', 'save', 'kill', 'powerUse', 'greed'];

/**
 * One lesson's modal: its title, text and (Learn to Play) what to do now, over the table (dimmed,
 * still in view). `card`: your card, for a real game's Powers lesson. `last`: Learn to Play's
 * last lesson, whose button finishes the tutorial.
 */
export function LessonModal({
  id,
  learn,
  card,
  last = false,
  onNext,
}: {
  id: LessonId;
  learn: boolean;
  card?: CardDef;
  last?: boolean;
  onNext: () => void;
}) {
  const how = (learn ? HOW_LEARN : HOW_GAME)[id];
  let body: ReactNode = <p className="lesson-body">{t(BODY[id] ?? `learn.${id}`, { n: MAX_SEQUENCES })}</p>;
  if (id === 'power' && !learn && card) {
    body = <PowerLine card={card} className="lesson-body lesson-power" before={t('learn.powerYours')} />;
  }
  return (
    <div className={`modal-backdrop lesson-backdrop${TOP.includes(id) ? ' top' : ''}`}>
      <div className="modal lesson-modal" role="dialog" aria-modal="true" aria-labelledby="lesson-title">
        <h2 id="lesson-title">{t(`lessonTitle.${id}`)}</h2>
        {body}
        {how && <p className="lesson-how">{t(how)}</p>}
        <button className="primary intro-next" autoFocus onClick={onNext}>
          {last ? t('learn.finish') : learn ? t('learn.gotIt') : t('common.next')}
        </button>
      </div>
    </div>
  );
}

/**
 * Rings that pulse round cards on the table (`data-card-of`): the lesson's subject, or the cards
 * to tap. `ids` null: every card.
 */
export function CardRings({ ids }: { ids: readonly number[] | null }) {
  const sel = ids === null ? '.table [data-card-of]' : ids.map((id) => `.table [data-card-of="${id}"]`).join(',');
  if (!sel) return null;
  return <style>{`${sel} { animation: lesson-ring 1.1s ease-in-out infinite; border-radius: 8px; }`}</style>;
}
